import { useSelector } from 'react-redux';
import { Store } from 'src/redux/Store';
import { ErrorBag } from 'src/models/ErrorModel';
import { FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { t } from 'src/lib/language';
import { FORM_ID } from 'src/models/FormModel';

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

interface Props {
  disabled?: boolean
  form: FORM_ID,
  name: string,
  onChange: (id:number) => void,
  value?: number,
}

interface CountryItem {
  id: number,
  label?: string,
}

const CountriesSelector = (props: Props) => {
  const classes = useStyles();

  const {
    disabled, form, name, onChange, value,
  } = props;

  const countries = useSelector((state: Store) => state.data.countries);
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const errors: ErrorBag =
    useSelector((state:Store) => state.errors[form] || {} as ErrorBag);

  const getCountryItems = () => {
    const items:CountryItem[] = [{ id: 0, label: undefined }];
    if (!countries.length) {
      return items;
    }
    countries.sort(
      (a, b) => a[`name_${lang}`].localeCompare(b[`name_${lang}`]),
    ).forEach((item: any) => {
      items.push({
        id: item.id,
        label: item[`name_${lang}`],
      });
    });
    return items;
  };

  const onValueChange = (item:CountryItem) => {
    if (!item || !item.id) {
      onChange(0);
      return null;
    }
    onChange(item.id as number);
    return null;
  };

  const getValue = (initialValue?: number) => {
    let result = {
      id: 0,
      label: undefined,
    } as CountryItem;
    if (!countries.length || !initialValue) {
      return result;
    }
    countries.forEach((item: any) => {
      if (item.id === initialValue) {
        result = {
          id: item.id,
          label: item[`name_${lang}`],
        } as CountryItem;
      }
    });
    return result;
  };

  const getOptionLabel = (option: CountryItem) => option.label || '';

  return (
    <FormControl
      variant="outlined"
      className={classes.formControl}
      key={`form-control-${name}`}>
      <Autocomplete
        disabled={disabled}
        value={getValue(value)}
        onChange={(e, item) => onValueChange(item as CountryItem)}
        options={getCountryItems() || []}
        getOptionLabel={(option: CountryItem) => getOptionLabel(option)}
        getOptionSelected={(option, item) => option.id === item.id}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={t(lang, 'forms.contact.country')}
            error={!!errors[name]} />
        )}
        key={`autocomplete-${name}`} />
    </FormControl>
  );
};

export default CountriesSelector;
//
